/* Console — the shell.
 *
 * Owns the brand, the module switcher, the sync pill and the toast. Everything
 * else on screen comes from whichever module is active: its `header` renders
 * into the top bar, its `page` fills the body. Nothing in here knows what a turn
 * is — adding a module means registering it in apps.js, not editing this file.
 *
 * The component only. Standing it up — boot order, plugin forms, mounting —
 * belongs to main.js, which is what index.html loads.
 */
import * as Vue from 'vue';
import { apps } from './apps.js';
import { AppSwitcher } from './components/app-switcher.js';
import { ModuleStub } from './components/module-stub.js';
import { StationSignIn } from './components/station-signin.js';
import { store } from './store.js';
import { versionCheck } from './version-check.js';

var App = {
  name: 'DreamDashConsole',
  components: {
    AppSwitcher: AppSwitcher,
    ModuleStub: ModuleStub,
    StationSignIn: StationSignIn
  },
  setup: function () {
    /* Signed out, the console is one thing: a sign-in.
     *
     * Every tt_* table is authenticated-only, so a signed-out shell was a top
     * bar of live-looking controls over an empty grid — a module switcher that
     * switched, a date that was today, an End day button — none of which could
     * read or write a single row. It looked like a salon with nothing booked
     * rather than like a station that is not signed in, and the only thing on
     * screen saying otherwise was a banner claiming Supabase was unreachable.
     *
     * Three states, not two. 'checking' is the stored session being restored,
     * which is what is true for the first moment of an ordinary boot; calling
     * it signed out would flash the sign-in page in the face of every station
     * that is already signed in, every time it loads.
     */
    var gate = Vue.computed(function () {
      if (store.auth.checking) return 'checking';
      return store.auth.user ? 'ready' : 'out';
    });

    var dateLabel = Vue.computed(function () {
      var p = store.state.businessDate.split('-');
      var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
      return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
    });

    var active = apps.active;

    // Whether a newer console is live than the one on this screen.
    versionCheck.start();

    /* The stub stands in for two different absences: a module that has not been
     * built, and one this salon is not licensed for. It says which — see
     * module-stub.js — and the shell does not need to, because in both cases
     * what belongs on screen is "not this, and here is why".
     */
    var pageComponent = Vue.computed(function () {
      var a = active.value;
      // No module at all: this salon is offered nothing, and the stub says so.
      if (!a) return ModuleStub;
      if (apps.locked(a)) return ModuleStub;
      return a.page || ModuleStub;
    });

    // A locked module's header goes with its page. The Turn Tracker's nav
    // carries live counts read out of the store, and leaving it in the top bar
    // above a card saying the module is not licensed would be the one part of
    // it still working.
    var headerComponent = Vue.computed(function () {
      var a = active.value;
      if (!a || apps.locked(a)) return null;
      return a.header || null;
    });

    /* Outranks both of the others, because it is the only one of the three that
       is not going to fix itself. Offline clears when the wifi comes back and
       "3 unsaved" clears when they go up; a refused write is already gone, and
       the queue behind it may well have drained perfectly, so this is the one
       state where a green "Synced" would be a lie. It names the count rather
       than the table — the table is in the title and the row is in the console
       — because the number is what tells the desk whether to stop and look. */
    var syncLabel = Vue.computed(function () {
      var st = store.syncStatus;
      if (st.dropped > 0) return st.dropped + ' not saved';
      if (st.pending > 0) return st.pending + ' unsaved';
      return st.online ? 'Synced' : 'Offline';
    });

    var syncTitle = Vue.computed(function () {
      var st = store.syncStatus;
      if (st.dropped > 0) {
        return st.dropped + ' change(s) were refused by the server and have been '
          + 'discarded — they will not be retried. Reload to see what actually '
          + 'saved.\n' + (st.lastDrop || '');
      }
      return st.lastError || 'Supabase';
    });

    // The tab is the one label a station sees while the console is behind
    // another window, so it names the module as well as the business. Not while
    // the gate is up: no module is on screen to be named, and a tab reading
    // "Turn Tracker" over a sign-in page is the same lie the shell used to tell.
    Vue.watchEffect(function () {
      var a = gate.value === 'ready' ? active.value : null;
      document.title = (a ? a.name + ' — ' : '') + store.businessName.value;
    });

    // The top bar is sticky, so it owns the first slice of the viewport for
    // good. Anything else that wants to fill the screen — the appointments
    // pane — has to subtract it, and it is not a fixed number: the bar wraps
    // to two lines on a narrow window.
    var topbarEl = Vue.ref(null);
    var topbarRo = null;

    function publishTopbar() {
      var el = topbarEl.value;
      if (!el) return;
      document.documentElement.style.setProperty(
        '--topbar-h', Math.round(el.getBoundingClientRect().height) + 'px');
    }

    /* Watched rather than hooked on mount, because the bar is no longer there
     * for the whole life of this component: the gate replaces it, so on a
     * station that boots signed out the header first appears at sign-in, long
     * after `onMounted` has run. An observer attached there would never see it,
     * `--topbar-h` would keep its `0px` fallback, and the appointments pane —
     * which is `position: sticky; top: var(--topbar-h)` — would slide under the
     * bar with no clue as to why.
     *
     * `flush: 'post'` so the element is in the DOM and has a height when this
     * measures it; `immediate` only to take the property back down on the way
     * into the gate, where nothing owns the top of the viewport. */
    Vue.watch(topbarEl, function (el) {
      if (topbarRo) { topbarRo.disconnect(); topbarRo = null; }
      window.removeEventListener('resize', publishTopbar);
      if (!el) {
        document.documentElement.style.removeProperty('--topbar-h');
        return;
      }
      publishTopbar();
      if (window.ResizeObserver) {
        topbarRo = new ResizeObserver(publishTopbar);
        topbarRo.observe(el);
      } else {
        window.addEventListener('resize', publishTopbar);
      }
    }, { immediate: true, flush: 'post' });

    Vue.onBeforeUnmount(function () {
      if (topbarRo) topbarRo.disconnect();
      window.removeEventListener('resize', publishTopbar);
    });

    return {
      active: active,
      gate: gate,
      topbarEl: topbarEl,
      businessName: store.businessName,
      pageComponent: pageComponent,
      headerComponent: headerComponent,
      logo: store.logo,
      ui: store.ui,
      support: store.support,
      syncStatus: store.syncStatus,
      syncLabel: syncLabel,
      syncTitle: syncTitle,
      signOut: store.signOut,
      update: versionCheck.state,
      reloadConsole: versionCheck.reload,
      dateLabel: dateLabel
    };
  },
  template: [
    /* Signed out. Deliberately the WHOLE screen — no top bar, no switcher, no
       sync pill, no board underneath. Every one of those needs a session to mean
       anything, and a disabled-looking console invites the front desk to try
       them. There is nothing else on this page to do. */
    '<main v-if="gate === \'out\'" class="signin-gate">',
    '  <div class="signin-gate__card">',
    '    <img v-if="logo.value" class="signin-gate__logo" :src="logo.value.dataUrl" :alt="businessName" />',
    '    <h1 v-else class="signin-gate__brand">{{ businessName }}</h1>',
    '    <p class="signin-gate__note">This station is signed out. Sign in with the',
    '      salon&rsquo;s station account to put the board on screen.</p>',
    '    <station-sign-in />',
    '  </div>',
    '</main>',
    '',
    /* Still restoring the stored session. Blank on purpose, and for one frame on
       an ordinary boot: the alternatives are flashing a sign-in page at a station
       that is signed in, or flashing an empty board at one that is not. */
    '<div v-else-if="gate === \'checking\'" class="signin-gate" aria-hidden="true"></div>',
    '',
    '<template v-else>',
    '<header class="topbar" ref="topbarEl">',
    /* Inside the sticky header, above everything else it holds. Two reasons it
       is here rather than a sibling: the header is what stays on screen when the
       board scrolls, and `--topbar-h` is measured from this element — a bar
       outside it would be counted by nothing and the appointments pane would run
       under the top of the window by its own height. */
    '  <div v-if="support.show" class="support-bar"',
    '    :class="{ \'is-urgent\': support.urgent, \'is-ended\': support.ended }" role="status">',
    '    <span class="support-bar__dot" aria-hidden="true"></span>',
    '    <p v-if="support.ended" class="support-bar__text">',
    '      <strong>Support session ended.</strong> This tab still saves into',
    '      {{ support.tenantName }} &mdash; close it.</p>',
    '    <p v-else class="support-bar__text">',
    '      <strong>Support session.</strong> You are standing in',
    '      {{ support.tenantName }}; anything done here is their day.</p>',
    '    <span v-if="!support.ended" class="support-bar__left">{{ support.label }} left</span>',
    '  </div>',
    /* Below the support bar, which outranks it. Asks rather than reloads: the
       desk may be in the middle of a sheet. See js/version-check.js. */
    '  <div v-if="update.behind" class="update-bar" role="status">',
    '    <p class="update-bar__text"><strong>A newer version of the console is live.</strong>',
    '      Refresh to get it &mdash; nothing on the board is lost.</p>',
    '    <button type="button" class="update-bar__btn" @click="reloadConsole">Refresh</button>',
    '  </div>',
    '  <div class="topbar__inner">',
    '    <div class="topbar__brand">',
    '      <img v-if="logo.value" class="topbar__logo" :src="logo.value.dataUrl" :alt="businessName" />',
    '      <h1 v-else>{{ businessName }}</h1>',
    '      <span class="topbar__sub">{{ dateLabel }}</span>',
    '    </div>',
    /* Beside the brand rather than out at the right-hand end. It is a state, not
       a control — the one thing in this bar nobody presses — and it belongs with
       the other things the bar states about itself. The cloud is decorative: the
       word and the colour carry the meaning, which is what keeps it readable to
       anyone who cannot tell the amber from the red. On a phone the whole pill
       collapses to a coloured dot and the glyph goes with it. */
    '    <span class="sync-pill" :class="{ \'is-off\': !syncStatus.online, \'is-pending\': syncStatus.pending > 0,',
    '        \'is-dropped\': syncStatus.dropped > 0 }"',
    '      :title="syncTitle">',
    '      <span class="sync-pill__cloud" aria-hidden="true">&#9729;</span>{{ syncLabel }}</span>',
    '    <app-switcher />',
    '    <component v-if="headerComponent" :is="headerComponent" />',
    /* Far right, and the quietest thing in the bar. It is pressed once a shift at
       most, and it is the only button up here that takes the entire console off
       screen — so it sits where nothing else is aimed, rather than next to the
       three tabs a thumb is already going for. */
    '    <button type="button" class="btn topbar__signout" @click="signOut"',
    '      title="Sign this station out. The board comes back off the same account.">',
    '      <span class="topbar__signout-icon" aria-hidden="true">&#9099;</span>',
    '      <span class="topbar__signout-label">Sign out</span></button>',
    '  </div>',
    '</header>',
    '',
    '<component v-if="pageComponent" :is="pageComponent" />',
    '',
    '<transition name="toast">',
    '  <div v-if="ui.toast" class="toast" :class="\'toast--\' + ui.toast.kind">{{ ui.toast.text }}</div>',
    '</transition>',
    '</template>'
  ].join('\n')
};

export { App };
